import React, { useRef, useState } from 'react';
import {
  withStyles,
} from '@material-ui/core';
import { useIntl } from 'react-intl';
import compose from 'recompose/compose';
import { getI18n } from '../../../../../utils/intl';
import styles from './styles';
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import {
  Clear as DeleteIcon,
  DragIndicator,
  Edit as EditIcon,
  WarningRounded as ErrorRoundedIcon,
  SwapVertSharp as SwapVertSharpIcon,
} from '@material-ui/icons';
import { useDrag, useDrop } from "react-dnd";
import {
  differenceBy,
  filter,
  find,
  forEach,
  map,
  some,
  sortBy,
} from 'lodash';
import PropTypes from 'prop-types';
import {
  DropDownMenu,
  FormButtonGroup,
} from '../../../../../components';
import EntityDropTarget from '../EntityDropTarget';
import EntityPicker from '../EntityPicker';
import PolicyTemplate from '../PolicyTemplate';
import { DndProvider } from "react-dnd";
import HTML5Backend from "react-dnd-html5-backend";
import {
  CATEGORY_CONFIG_ORDER,
  CATEGORY_CONFIG_USAGE,
  ENTITY_PICKER_TYPE_TEMPLATE,
  TEMPLATE_ERRORS_FILED_MISSING_REQUIRED_FIELD_VALUE,
  USAGE_OPTION_AT_LEAST_ONE_OPTION,
  USAGE_OPTION_OPTIONAL,
  VIOLATION_LEVEL_PUBLISHER_OVERRIDABLE,
  getComplianceToggleOptions,
} from '../constants';
import clsx from 'clsx';

export const Category = (props) => {
  const {
    addTemplate,
    alwaysAllowOrderingTemplates,
    associatedTemplates,
    availableTemplates,
    canRemove,
    categoryDetails,
    categoryIndex,
    categoryTemplatesErrors,
    categoryWiseValidationErrorsMap,
    classes,
    complianceLevel,
    complianceToggleStatus,
    expandAllTemplates,
    handleChangeDynamicForm,
    handleCategoryCompliantToggle,
    isNonCompliantCategory,
    moveTemplate,
    name,
    onMoveCategory,
    removeCategory,
    removeTemplate,
    showCategoryCompliance,
    uuid,
    onCopyArguments,
    onPasteArguments,
    argumentClipboard = {},
  } = props;
  const intl = getI18n(useIntl());
  const [showCategoryDropTargets, setShowCategoryDropTargets] = useState(false);

  const isUnavailableTemplate = (template) => (!template.name);

  const ref = useRef(null);
  const [{ handlerId }, drop] = useDrop({
    accept: ['category'],
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.categoryIndex;
      const hoverIndex = categoryIndex;
      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      setTimeout(() => {
        onMoveCategory(dragIndex, hoverIndex);
      });
      item.categoryIndex = hoverIndex;
    },
  });

  const [, drag] = useDrag({
    canDrag: categoryDetails.flexible,
    item: {
      categoryIndex,
      type: "category",
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  // const opacity = isDragging ? 0 : 1;
  drag(drop(ref));

  const onTemplateDrop = (categoryUuid, templateId, index) => {
    const selectedTemplate = find(availableTemplates, template => template.uuid === templateId);
    addTemplate(categoryUuid, selectedTemplate, index);
  };

  const renderTemplate = (apiCategoryTemplate, index, templateErrors, categoryUuid) =>
    (<PolicyTemplate
      canMove={!categoryDetails.enforceTemplateOrdering ||
        apiCategoryTemplate.flexible || alwaysAllowOrderingTemplates}
      categoryId={uuid}
      categoryIndex={categoryIndex}
      count={associatedTemplates.length}
      expandTemplate={expandAllTemplates}
      handleChangeDynamicForm={(val, name, index) =>
        handleChangeDynamicForm(categoryUuid, val, name, index)
      }
      index={index}
      item={apiCategoryTemplate}
      key={`${categoryUuid}-${index}`}
      onDeleteItem={({ uuid }, i) => removeTemplate(categoryUuid, uuid, i)}
      onMoveItem={(dragIndex, hoverIndex) => moveTemplate(categoryUuid, dragIndex, hoverIndex)}
      templateErrors={templateErrors}
      onCopyArguments={onCopyArguments}
      onPasteArguments={onPasteArguments}
      isClipboardAvailable={argumentClipboard[apiCategoryTemplate.uuid]}
    />);

  const renderCategoryTemplates = (associatedTemplates, categoryUuid) => {
    const renderDropTarget = (index) => (
      <EntityDropTarget
        addEntity={(uuid, index) => {
          onTemplateDrop(categoryUuid, uuid, index);
          setShowCategoryDropTargets(false);
        }}
        index={index}
        showDropTargets={showCategoryDropTargets}
        type={ENTITY_PICKER_TYPE_TEMPLATE}
      />);
    if (associatedTemplates.length === 0) {
      return (<div>
        {renderDropTarget(0)}
      </div>);
    }
    return (<div>
      {renderDropTarget(0)}
      {associatedTemplates.map(
        (apiCategoryTemplate, index) => {
          const templateErrors = categoryTemplatesErrors[index] || [];
          return (<div key={`${uuid}-${categoryIndex}-${apiCategoryTemplate.uuid}-${index}`}>
            {renderTemplate(apiCategoryTemplate, index, templateErrors, categoryUuid)}
            {renderDropTarget(index + 1)}
          </div>)
        },
      )}
    </div>);
  };

  const renderAddTemplatesDropdown = () => {
    const handleChange = (uuid, value) => {
      const selectedTemplate =
        find(availableTemplates, item => item.uuid === value);
      addTemplate(uuid, selectedTemplate);
    }
    const fixedTemplates = [];
    const flexibleTemplates = [];
    forEach(availableTemplates, item => {
      if (item.flexible) {
        flexibleTemplates.push(item);
      } else {
        fixedTemplates.push(item);
      }
    });
    const sortedFixedTemplates = sortBy(fixedTemplates, item => item.ordinal);
    let options = [...sortedFixedTemplates, ...flexibleTemplates];
    if (categoryDetails.enforceTemplateOrdering) {
      options = map(options, item => ({
        ...item,
        disabled: !item.flexible &&
          find(associatedTemplates, template => template.uuid === item.uuid),
      }));
    }
    return (<DropDownMenu
      handleChange={(value) => handleChange(uuid, value)}
      customClasses={{
        menuListRoot: classes.templatesMenuList,
        popper: classes.popper,
      }}
      data={options}
      id="add-templates-dropdown"
      label={intl.getI18nMessage('label.category.details.templates.add.templates.button')}
      value={null}
    />)
  };

  const renderDeleteIcon = () => {
    return (<IconButton
        className={canRemove ? classes.iconRoot : classes.disableDeleteIcon}
        data-apim-test={`category-${categoryIndex}-delete`}
        onClick={removeCategory}
        onFocus={e => e.stopPropagation()}
        size="small"
      >
        <DeleteIcon />
      </IconButton>)
    };
  
  const renderCompliantError = () => {
    const info = intl.getI18nMessage(
      'label.api.edit.category.compliance.non.compliant.error.text',
    );
    return (<div
        className={classes.titleStatusIcon}
        data-apim-test='non-compliant-category-error-icon'
      >
      <Tooltip title={info} arrow>
        <ErrorRoundedIcon className={classes.complianceError} />
      </Tooltip>
    </div>);
  }

  const checkAndRenderOrderingIcon = () => {
    if (categoryDetails.enforceTemplateOrdering &&
      !categoryWiseValidationErrorsMap[CATEGORY_CONFIG_ORDER]) {
      return null;
    }
    let iconClassName = classes.complianceNoIssue;
    let info = intl.getI18nMessage('label.api.edit.category.compliance.icon.order.help.text');
    let issueType = 'none';
    if (categoryWiseValidationErrorsMap[CATEGORY_CONFIG_ORDER]) {
      if (complianceLevel === VIOLATION_LEVEL_PUBLISHER_OVERRIDABLE) {
        info = intl.getI18nMessage('label.api.edit.category.compliance.icon.order.warning.help.text');
        iconClassName = classes.complianceWarning;
        issueType = 'warning';
      } else {
        info = intl.getI18nMessage('label.api.edit.category.compliance.icon.order.error.help.text');
        iconClassName = classes.complianceError;
        issueType = 'error';
      }
    }
    return (<div className={classes.titleStatusIcon}>
      <Tooltip title={info} arrow>
        <SwapVertSharpIcon
          className={iconClassName}
          data-apim-test='category-order-status-icon'
          data-issue-type={issueType}
        />
      </Tooltip>
    </div>);
  };

  const checkAndRenderUsageIcon = () => {
    if (categoryDetails.templateUsageRestriction !== USAGE_OPTION_OPTIONAL) {
      const label = categoryDetails.templateUsageRestriction === USAGE_OPTION_AT_LEAST_ONE_OPTION ?
        '1+' : 'ALL';
      let iconClassName = classes.chipSuccess;
      const info = categoryDetails.templateUsageRestriction === USAGE_OPTION_AT_LEAST_ONE_OPTION ?
        intl.getI18nMessage('label.api.edit.category.compliance.icon.usage.atleast.one.help.text')
        :
        intl.getI18nMessage('label.api.edit.category.compliance.icon.usage.all.help.text');
      let issueType = 'none';
      if (categoryWiseValidationErrorsMap[CATEGORY_CONFIG_USAGE]) {
        if (complianceLevel === VIOLATION_LEVEL_PUBLISHER_OVERRIDABLE) {
          iconClassName = classes.chipWarn;
          issueType = 'warning';
        } else {
          iconClassName = classes.chipError;
          issueType = 'error';
        }
      }
      return (<div className={classes.titleStatusIcon}>
        <Tooltip title={info} arrow>
          <Chip
            classes={{
              label: iconClassName,
              outlined: iconClassName,
            }}
            className={iconClassName}
            label={label}
            variant="outlined"
            data-apim-test='category-usage-status-icon'
            data-issue-type={issueType}
          />
        </Tooltip>
      </div>);
    }
    return null;
  };

  const checkAndRenderTemplateErrorIcon = () => {
    const categoryHasMissingFields = some(categoryTemplatesErrors, templateWiseErrors => {
      return some(templateWiseErrors, argError =>
          argError[TEMPLATE_ERRORS_FILED_MISSING_REQUIRED_FIELD_VALUE],
        );
    });
    const hasUnavaliableTemplates =
      !!find(associatedTemplates, template => isUnavailableTemplate(template));
    const hasTemplateError = categoryHasMissingFields || hasUnavaliableTemplates;
    if (hasTemplateError) {
      const info = intl.getI18nMessage(
        'label.api.edit.category.compliance.icon.template.errors.error.help.text',
      );
      return (<div className={classes.titleStatusIcon}>
        <Tooltip title={info} arrow>
          <EditIcon
            className={classes.complianceError}
            data-apim-test='category-template-error-icon'
          />
        </Tooltip>
      </div>);
    }
    return null;
  };

  const renderTitleStatusIcons = () => {
    const categoryHasTemplates = associatedTemplates && (associatedTemplates.length > 0);
    if (isNonCompliantCategory && categoryHasTemplates) {
      return (
        <div className={classes.titleStatusIconsContainer}>
          {renderCompliantError()}
        </div>);
    }

    return (
      <div className={classes.titleStatusIconsContainer}>
        {checkAndRenderOrderingIcon()}
        {checkAndRenderUsageIcon()}
        {checkAndRenderTemplateErrorIcon()}
      </div>
    );
  };

  const renderTemplatePicker = () => {
    const flexibleTemplates = filter(availableTemplates, template => template.flexible);
    const templatesToBeIncluded =
      differenceBy(flexibleTemplates, associatedTemplates, item => item.uuid)
    if (categoryDetails.templateUsageRestriction === USAGE_OPTION_OPTIONAL ||
      templatesToBeIncluded.length === 0 ||
      ((categoryDetails.templateUsageRestriction === USAGE_OPTION_AT_LEAST_ONE_OPTION) &&
      (associatedTemplates.length > 0))) {
      return null;
    }

    const message = categoryDetails.templateUsageRestriction === USAGE_OPTION_AT_LEAST_ONE_OPTION
      ? intl.getI18nMessage('label.api.edit.entity.picker.template.at.least.one.title.text')
        :
        intl.getI18nMessage('label.api.edit.entity.picker.template.all.title.text');
    return (<EntityPicker
      categoryIndex={categoryIndex}
      entities={templatesToBeIncluded}
      hasError={complianceLevel !== VIOLATION_LEVEL_PUBLISHER_OVERRIDABLE}
      message={message}
      onDrag={setShowCategoryDropTargets}
      type={ENTITY_PICKER_TYPE_TEMPLATE}
    />);
  };

  const categoryHeaderClasses = clsx(classes.categoryHeader,
    categoryDetails.flexible ? classes.flexibleCategoryHeader : '');

  return (
    <div
      className={classes.categoryBox}
      data-apim-test={`category-${uuid}-box`}
      key={`${uuid}-${categoryIndex}`}
    >
      <div
        className={categoryHeaderClasses}
        data-apim-test={`category-${uuid}-header`}
        data-handler-id={handlerId}
        ref={ref}
      >
        <div className={classes.categoryHeaderLeft}>
          <IconButton
            className={categoryDetails.flexible ? classes.iconRoot : classes.fixedCategoryDragIcon}
            data-apim-test={`template-${categoryIndex}-drag`}
            size="small"
          >
            <DragIndicator />
          </IconButton>
          <div className={classes.categoryTitle}>
            {name}
          </div>
          <div>
            {renderTitleStatusIcons()}
          </div>
        </div>
        <div className={classes.categoryTitleBtns}>
          {(!isNonCompliantCategory && showCategoryCompliance) &&
            <FormButtonGroup
              id={`category-${uuid}-compliance-toggle-btn-group`}
              name={''}
              value={complianceToggleStatus}
              buttonGroupFieldClass={classes.complianceBtnsGroup}
              data={getComplianceToggleOptions(intl)}
              handleChange={(value) => handleCategoryCompliantToggle(uuid, value)}
              filedContainerClass={classes.complianceBtnsGroupContainer}
            />}
          {renderAddTemplatesDropdown()}
          {renderDeleteIcon()}
        </div>
      </div>
      <Divider />
      <Box>
        <div data-apim-test={`category-${uuid}-included-templates`}>
          <DndProvider backend={HTML5Backend}>
            {renderTemplatePicker()}
            {renderCategoryTemplates(associatedTemplates, uuid)}
          </DndProvider>
        </div>
      </Box>
    </div>
  )
};

Category.propTypes = {
  addTemplate: PropTypes.func,
  alwaysAllowOrderingTemplates: PropTypes.bool,
  associatedTemplates: PropTypes.array,
  availableTemplates: PropTypes.array,
  canRemove: PropTypes.bool,
  categoryDetails: PropTypes.object,
  categoryIndex: PropTypes.number,
  categoryTemplatesErrors: PropTypes.array,
  categoryWiseValidationErrorsMap: PropTypes.object,
  classes: PropTypes.object,
  complianceLevel: PropTypes.string,
  complianceToggleStatus: PropTypes.string,
  expandAllTemplates: PropTypes.bool,
  handleChangeDynamicForm: PropTypes.func,
  handleCategoryCompliantToggle: PropTypes.func,
  isNonCompliantCategory: PropTypes.bool,
  moveTemplate: PropTypes.func,
  name: PropTypes.string,
  onMoveCategory: PropTypes.func,
  removeCategory: PropTypes.func,
  removeTemplate: PropTypes.func,
  showCategoryCompliance: PropTypes.bool,
  uuid: PropTypes.string,
  onCopyArguments: PropTypes.func,
  onPasteArguments: PropTypes.func,
  argumentClipboard: PropTypes.object,
};

Category.displayName = 'Category';

export default compose(
  withStyles(styles),
)(Category);
